/**
 * Awards (FSD 4.2.4 Recommend / Approve / Convert / Notify / Cancel, P45 PS6-U69) — the
 * feature's own wrappers over B73's operations.
 *
 * The life of an award: recommended (amendable by its recommender) → submitted to approval
 * (`procurement.award`, decided in the Approvals Inbox by somebody other than the recommender)
 * → approved → converted to draft purchase orders or draft price agreements. Unsuccessful
 * bidders are told once the award is approved. A rejected award ends cancelled.
 *
 * Every write takes the caller's held `idempotencyKey` (X1); nothing here mints one.
 */

import {
  procurementAwardCancel,
  procurementAwardConvert,
  procurementAwardGet,
  procurementAwardList,
  procurementAwardNotifyUnsuccessful,
  procurementAwardSubmit,
  procurementAwardUpdate,
  procurementRfqAwardRecommend,
  type ListProcurementAwardsQuery,
  type PageProcurementAwardRowDTO,
  type ProcurementAwardCancelRequest,
  type ProcurementAwardConvertRequest,
  type ProcurementAwardDTO,
  type ProcurementAwardFeedbackRequest,
  type ProcurementAwardRecommendRequest,
  type ProcurementAwardUpdateRequest,
} from '@erpx/api'

import { api } from '@/api/client'

/** Permission codes the award screens check (mirrors `internal/procurement`). */
export const AWARD_PERMISSIONS = {
  /** Reads: awards and the comparison behind them. */
  rfqRead: 'procurement.rfq.read',
  /** Recommend, amend (recommender only), submit and cancel an award. */
  recommend: 'procurement.award.recommend',
  /** Decide an award in the Approvals Inbox. SoD partner of `recommend`. */
  approve: 'procurement.award.approve',
  /** Convert an approved award; tell the unsuccessful bidders. */
  convert: 'procurement.award.convert',
  /** Converting to a price agreement also needs this (CreateContract re-checks it). */
  contractManage: 'procurement.contract.manage',
} as const

export const AWARD_STATUSES = [
  'recommended',
  'pending_approval',
  'approved',
  'converted',
  'cancelled',
] as const
export type AwardStatusValue = (typeof AWARD_STATUSES)[number]

export const AWARD_KEYS = {
  all: ['procurement', 'awards'] as const,
  page: (params: Record<string, unknown>) => ['procurement', 'awards', 'list', params] as const,
  ofRfq: (rfqId: number) => ['procurement', 'awards', 'rfq', rfqId] as const,
  one: (id: number) => ['procurement', 'awards', 'detail', id] as const,
} as const

export interface AwardFilters {
  companyId?: number | null
  rfqId?: number
  vendorId?: number
  status?: AwardStatusValue | null
  cursor?: string | null
  limit?: number
}

export function listAwards(filters: AwardFilters): Promise<PageProcurementAwardRowDTO> {
  const query: ListProcurementAwardsQuery = {
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.cursor === undefined || filters.cursor === null || filters.cursor === ''
      ? {}
      : { cursor: filters.cursor }),
    ...(filters.companyId === undefined || filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.rfqId === undefined ? {} : { rfqId: filters.rfqId }),
    ...(filters.vendorId === undefined ? {} : { vendorId: filters.vendorId }),
    ...(filters.status === undefined || filters.status === null ? {} : { status: filters.status }),
  }
  return procurementAwardList(api(), query)
}

export function getAward(id: number): Promise<ProcurementAwardDTO> {
  return procurementAwardGet(api(), { id: String(id) })
}

/** Recommend an award from a comparison version: one supplier (`vendorId`) or a split. */
export function recommendAward(
  rfqId: number,
  body: ProcurementAwardRecommendRequest,
  idempotencyKey: string,
): Promise<ProcurementAwardDTO> {
  return procurementRfqAwardRecommend(api(), { id: String(rfqId) }, body, idempotencyKey)
}

/** Amend a recommendation that has not been submitted. Only its recommender may. */
export function updateAward(
  id: number,
  body: ProcurementAwardUpdateRequest,
  idempotencyKey: string,
): Promise<ProcurementAwardDTO> {
  return procurementAwardUpdate(api(), { id: String(id) }, body, idempotencyKey)
}

/** Submit to approval: the pack becomes immutable. */
export function submitAward(id: number, idempotencyKey: string): Promise<ProcurementAwardDTO> {
  return procurementAwardSubmit(api(), { id: String(id) }, idempotencyKey)
}

/** Convert an approved award to draft purchase orders (`po`) or draft price agreements (`contract`). */
export function convertAward(
  id: number,
  body: ProcurementAwardConvertRequest,
  idempotencyKey: string,
): Promise<ProcurementAwardDTO> {
  return procurementAwardConvert(api(), { id: String(id) }, body, idempotencyKey)
}

/** Tell unsuccessful bidders, each once, with a reason. */
export function notifyUnsuccessful(
  id: number,
  body: ProcurementAwardFeedbackRequest,
  idempotencyKey: string,
): Promise<ProcurementAwardDTO> {
  return procurementAwardNotifyUnsuccessful(api(), { id: String(id) }, body, idempotencyKey)
}

/** Cancel an award that is not converted (an approval in flight is withdrawn). */
export function cancelAward(
  id: number,
  body: ProcurementAwardCancelRequest,
  idempotencyKey: string,
): Promise<ProcurementAwardDTO> {
  return procurementAwardCancel(api(), { id: String(id) }, body, idempotencyKey)
}
